/* eslint-disable react-hooks/exhaustive-deps */
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { dataAttr } from '@tonic-one/utils';
import {
  Link,
  Checkbox,
  Table,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TableScrollbar,
  Icon,
  Stack,
  Tooltip,
  useColorMode,
} from '@tonic-one/react';
import { DeleteIcon, FileCopyOIcon } from '@tonic-one/react-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from '@Components/IconButton';
import {
  DlpKeywordListModalNameEnum,
  DlpKeywordListModalState,
} from '../../type';
import { PermissionNameEnum } from '@Types/permissions';
import { usePermissionStore } from '@Models/usePermissionModel';
import { DlpKeyword, DpBuiltinEnum, FilterParams } from '@Policy/utils/types';
import { PolicyApi } from '@Apis';
import { buildInStringMap } from '@Policy/utils/constants';
import i18next from 'i18next';

const KeywordTable = ({
  data,
  filterParams,
  refreshTable,
  setModalState,
  setSelectedRows,
}: {
  data: DlpKeyword[];
  filterParams: FilterParams;
  refreshTable: () => void;
  setModalState: (v: DlpKeywordListModalState) => void;
  setSelectedRows: (v: DlpKeyword[]) => void;
}) => {
  const [colorMode] = useColorMode();
  const { t } = useTranslation();
  const { widthEditablePermission } = usePermissionStore();
  const dlpEditPermission = widthEditablePermission(
    PermissionNameEnum.dlpPolicy
  );
  const hoverBackgroundColor = {
    dark: 'rgba(255, 255, 255, 0.12)',
    light: 'rgba(0, 0, 0, 0.12)',
  }[colorMode];

  const openDeleteModal = useCallback(
    (cfgIndex: string, dpKeywordName: string, dpKeywordDesc: string) => {
      setModalState({
        name: DlpKeywordListModalNameEnum.Delete,
        data: {
          cfgIndex,
          dpKeywordName,
          dpKeywordDesc,
        },
      });
    },
    []
  );

  const openEditModal = useCallback((cfgIndex: string) => {
    setModalState({
      name: DlpKeywordListModalNameEnum.Edit,
      data: {
        cfgIndex,
      },
    });
  }, []);

  const duplicateDlpKeyword = useCallback(
    (keywordId: string, keywordName: string) => {
      PolicyApi.duplicateDlpKeywordById(
        keywordId,
        keywordName,
        i18next.language
      )
        .then(() => {
          window.showNotification({
            titleMsg: t('policy.dataIdentifiers.keywordLists.copySuccessfully'),
            severity: 'success',
          });
        })
        .catch((error) => {
          console.error(error);
          window.showNotification({
            titleMsg: t('common.apiException.internalError'),
            severity: 'error',
          });
        })
        .finally(() => {
          setRowSelection({});
          refreshTable();
        });
    },
    []
  );

  const columns: ColumnDef<DlpKeyword>[] = useMemo(() => {
    return [
      {
        id: 'selection',
        accessorKey: 'cfgIndex',
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => {
          return (
            <Checkbox
              checked={row.getIsSelected()}
              disabled={!row.getCanSelect()}
              indeterminate={row.getIsSomeSelected()}
              onChange={row.getToggleSelectedHandler()}
            />
          );
        },
        meta: {
          customColumnStyle: {
            width: '48px',
          },
        },
      },
      {
        header: t('common.label.enterName'),
        accessorKey: 'dpKeywordName',
        cell: ({ row }) => {
          const { dpKeywordName, cfgIndex } = row.original;
          return (
            <Link
              onClick={() => {
                openEditModal(cfgIndex);
              }}
            >
              {dpKeywordName}
            </Link>
          );
        },
        meta: {
          customColumnStyle: {
            minWidth: '100px',
            width: '25%',
          },
        },
      },
      {
        header: t('common.label.description'),
        accessorKey: 'dpKeywordDesc',
        meta: {
          customColumnStyle: {
            minWidth: '100px',
            width: '40%',
          },
        },
      },
      {
        header: t('common.label.type'),
        accessorKey: 'dpKeywordBuiltin',
        cell: ({ row }) => {
          const { dpKeywordBuiltin } = row.original;
          return t(buildInStringMap.get(dpKeywordBuiltin) as string);
        },
        meta: {
          customColumnStyle: {
            minWidth: '100px',
            width: '25%',
          },
        },
      },
      {
        header: '',
        accessorKey: 'cfgIndex',
        cell: ({ row }) => {
          const { cfgIndex, dpKeywordName, dpKeywordDesc, dpKeywordBuiltin } =
            row.original;
          return (
            <PolicyActionCell
              cfgIndex={row.original.cfgIndex}
              dpKeywordName={row.original.dpKeywordName}
              copyFn={duplicateDlpKeyword}
              deleteFn={() => {
                if (!dlpEditPermission) return;
                openDeleteModal(cfgIndex, dpKeywordName, dpKeywordDesc);
              }}
              dlpEditPermission={dlpEditPermission}
              dpBuiltinEnum={dpKeywordBuiltin}
            />
          );
        },
        meta: {
          customColumnStyle: {
            minWidth: '80px',
            width: '5px',
          },
        },
      },
    ];
  }, []);

  const filteredData = useMemo(() => {
    const dataWithKeyword: DlpKeyword[] = [];
    if (filterParams.keyword !== '') {
      data.forEach((item) => {
        if (
          (item.dpKeywordName + ' ' + item.dpKeywordDesc)
            .toLowerCase()
            .includes(filterParams.keyword.toLowerCase())
        ) {
          dataWithKeyword.push(item);
        }
      });
    } else {
      dataWithKeyword.push(...data);
    }

    const dataWithPaging = dataWithKeyword.slice(
      (filterParams.offset - 1) * filterParams.size,
      filterParams.offset * filterParams.size
    );

    return dataWithPaging;
  }, [filterParams, data]);

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  useEffect(() => {
    console.log(`rowSelection`, rowSelection, Object.keys(rowSelection));
    setSelectedRows(
      filteredData.filter((v) => {
        return rowSelection[v.cfgIndex] === true;
      })
    );
  }, [rowSelection]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      rowSelection,
    },
    getRowId: (row) => row.cfgIndex,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Table size="md" layout="flexbox" width="100%" height="100%">
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableHeaderRow key={headerGroup.id} width="100%">
            {headerGroup.headers.map((header) => {
              const styleProps = {
                minWidth: header.column.columnDef.minSize,
                width: header.getSize(),
                ...header.column.columnDef.meta?.customColumnStyle,
              };
              const content = flexRender(
                header.column.columnDef.header,
                header.getContext()
              );
              return (
                <TableHeaderCell key={header.id} {...styleProps}>
                  {content}
                </TableHeaderCell>
              );
            })}
          </TableHeaderRow>
        ))}
      </TableHeader>
      <TableScrollbar height="calc(100% - 36px - 48px)" overflow="visible">
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              width="100%"
              key={row.id}
              data-selected={dataAttr(row.getIsSelected())}
              _hover={{
                backgroundColor: hoverBackgroundColor,
              }}
            >
              {row.getVisibleCells().map((cell) => {
                const styleProps = {
                  minWidth: cell.column.columnDef.minSize,
                  width: cell.column.getSize(),
                  ...cell.column.columnDef.meta?.customColumnStyle,
                };
                return (
                  <TableCell key={cell.id} {...styleProps}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </TableScrollbar>
    </Table>
  );
};

const PolicyActionCell = ({
  cfgIndex,
  dpKeywordName,
  deleteFn,
  copyFn,
  dlpEditPermission,
  dpBuiltinEnum,
}: {
  cfgIndex: string;
  dpKeywordName: string;
  deleteFn: () => void;
  copyFn: (keywordId: string, KeywordName: string) => void;
  dlpEditPermission: boolean;
  dpBuiltinEnum: DpBuiltinEnum;
}) => {
  const { t } = useTranslation();
  const PopperProps = {
    usePortal: true,
  };
  const onCopyClick = () => {
    console.log('copy', { cfgIndex, dpKeywordName });
    copyFn(cfgIndex, dpKeywordName);
  };
  const onDeleteClick = () => {
    console.log('delete', { cfgIndex, dpKeywordName });
    deleteFn();
  };
  return (
    <Stack direction="row" flexWrap="nowrap" spacing="6px">
      <IconButton onClick={onCopyClick} disabled={!dlpEditPermission}>
        <Tooltip label={t('policy.tooltip.copy')} PopperProps={PopperProps}>
          <Icon as={FileCopyOIcon} />
        </Tooltip>
      </IconButton>
      <IconButton
        onClick={onDeleteClick}
        disabled={
          !dlpEditPermission || dpBuiltinEnum === DpBuiltinEnum.Predefined
        }
      >
        <Tooltip label={t('policy.tooltip.delete')} PopperProps={PopperProps}>
          <Icon as={DeleteIcon} />
        </Tooltip>
      </IconButton>
    </Stack>
  );
};

export default KeywordTable;
